<template>
  <section class="form-list">
    <el-form
      ref="form"
      :model="post"
      label-position="right"
      label-width="130px"
      class="form"
      :rules="rules"
    >
      <el-form-item label="申请时间">{{ post.add_time | timeToYmdHi }}</el-form-item>

      <el-form-item label="申请者">
        <v-avatar-nickname
          :value="post.admin_avatar"
          :nickname="post.admin_nickname"
          :is-center="false"
        />
      </el-form-item>

      <el-form-item label="申请门店">{{ post.store_name }}</el-form-item>

      <el-form-item label="限制使用门店">
        <el-tag v-if="post.is_store_limited" size="small" type="warning">{{ post.limit_store_name }}</el-tag>
        <span v-else class="info-color">全部门店</span>
      </el-form-item>

      <el-form-item label="申请原因">
        <span>{{ post.apply_remark || '' }}</span>
      </el-form-item>

      <el-form-item label="备注文件">
        <v-remark-file-list v-if="post.remark_files && post.remark_files.length" :files="post.remark_files"/>
        <span v-else class="info-color">未上传</span>
      </el-form-item>

      <el-form-item label="所属学员">
        <v-avatar-nickname
          :value="post.selfie"
          :nickname="post.student_name"
          :link-page-name="RouteConfig.studentDetail.name"
          :link-page-id="post.student_id"
          :avatar-size="50"
        />
      </el-form-item>

      <el-form-item label="代金券类型">{{ post.voucher_type_name }}</el-form-item>

      <el-form-item label="唯一性">
        <el-tag :type="post.is_unique === 1 ? 'warning' : 'success'" size="small">
          {{ post.is_unique === 1 ? '每人限领一张' : '可重复拥有' }}
        </el-tag>
        <div class="info-color form-item-tip">以代金券类型当前设置为准，审核通过时按此规则校验</div>
      </el-form-item>

      <el-form-item label="面额">{{ post.amount_in_cents | toYuan }} 元</el-form-item>

      <el-form-item label="有效时长">
        <span v-if="post.expire_mode === 1 && post.fixed_expire_time">{{ post.fixed_expire_time | timeToYmdHi }} 过期</span>
        <span v-else>{{ post.valid_duration_seconds | formatDuration }}</span>
      </el-form-item>

      <el-form-item label="发放方式">
        <span v-for="state in findVoucherIssueType(post.issue_type)" :key="state.value">
          {{ state.name }}
        </span>
      </el-form-item>

      <el-form-item label="审核状态">
        <span v-if="!isLoading && !isLoadingError" :class="`${applyState.tagType}-color`">{{ applyState.name }}</span>
      </el-form-item>

      <template v-if="!isLoading && !isLoadingError && applyState.timeLabel && (canViewReviewInfo)">
        <el-form-item :label="applyState.timeLabel">{{ post.update_time | timeToYmdHi }}</el-form-item>
        <el-form-item :label="applyState.personLabel">
          <v-avatar-nickname
            :value="post.admin_avatar_update"
            :nickname="post.admin_nickname_update"
            :is-center="false"
          />
        </el-form-item>
      </template>

      <el-form-item v-if="applyState.value === applyStates.rejected.value && (canViewReviewInfo)" label="驳回原因">
        <span class="danger-color">{{ post.review_remark || '' }}</span>
      </el-form-item>

      <el-form-item v-if="applyState.value === applyStates.passed.value && isAdministrator" label="审核备注">
        <span v-if="post.review_remark" class="danger-color">{{ post.review_remark }}</span>
        <span v-else class="info-color">未填写</span>
      </el-form-item>

      <template v-if="!isLoading && !isLoadingError && post.apply_state === applyStates.review.value">
        <template v-if="isAdministrator">
          <el-form-item label="审核结果">
            <el-radio v-model="isReject" :label="0" :disabled="isExamining">通过</el-radio>
            <el-radio v-model="isReject" :label="1" :disabled="isExamining">驳回</el-radio>
          </el-form-item>

          <el-form-item :label="isReject ? '驳回原因' : '审核备注'" prop="review_remark">
            <el-input
              class="clearable-word-limit-input"
              v-model="post.review_remark"
              maxlength="200"
              :placeholder="isReject ? '请输入驳回原因' : '请输入审核备注'"
              show-word-limit
              clearable
              :disabled="isExamining"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="handleExamine" :loading="isExamining">完成审核</el-button>
            <el-button @click="RouteConfig.voucherAddApplyList.back">返回</el-button>
          </el-form-item>
        </template>
        <el-form-item v-else>
          <el-button type="primary" @click="handleRevoke" :loading="isRevoking">撤回</el-button>
          <el-button @click="RouteConfig.voucherAddApplyList.back">返回</el-button>
        </el-form-item>
      </template>
      <el-form-item v-else>
        <el-button @click="RouteConfig.voucherAddApplyList.back">返回</el-button>
      </el-form-item>
    </el-form>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { show, update, destroy } from '@/api/voucher-add-apply';
import { store as passApply } from '@/api/voucher-add-record';
import { applyStates, findApplyState } from '@/enums/applyStates';
import { findVoucherIssueType } from '@/enums/voucherIssueTypes';
import { mapGetters } from 'vuex';

export default {
  name: RouteConfig.voucherAddApplyDetail.name,
  data: () => ({
    RouteConfig,
    applyStates,
    isLoading: false,
    isLoadingError: false,
    isExamining: false,
    isReject: 0,
    isRevoking: false,
    post: {
      add_time: 0,
      update_time: 0,
      student_id: 0,
      student_name: '',
      selfie: '',
      voucher_type_name: '',
      amount_in_cents: 0,
      is_unique: 0,
      valid_duration_seconds: 0,
      expire_mode: 0,
      fixed_expire_time: 0,
      issue_type: 0,
      apply_state: applyStates.review.value,
      apply_remark: '',
      admin_avatar: '',
      admin_nickname: '',
      admin_avatar_update: '',
      admin_nickname_update: '',
      store_name: '',
      review_remark: '',
      store_id: 0,
      remark_files: [],
    },
  }),
  computed: {
    rules() {
      return this.isReject
        ? { review_remark: [{ required: true, message: '请输入驳回原因', trigger: 'change' }] }
        : {};
    },
    ...mapGetters(['isAdministrator', 'userStoreId']),
    canViewReviewInfo() {
      return this.isAdministrator || this.userStoreId === this.post.store_id;
    },
    id() {
      return +this.$route.params.id || 0;
    },
    applyState() {
      return findApplyState(this.post.apply_state)[0] || {};
    },
  },
  beforeMount() {
    this.show();
  },
  methods: {
    findVoucherIssueType,
    async show() {
      this.isLoadingError = false;
      this.isLoading = true;
      const { isError, data } = await show(this.id);
      this.isLoading = false;
      if (isError || !data) return this.isLoadingError = true;

      const { post } = this;
      const updateData = {};
      Object.keys(data).forEach(key => {
        if (key in post) updateData[key] = data[key];
      });
      Object.assign(post, updateData);
    },
    handleExamine() {
      if (this.isExamining) return;

      if (this.isReject) {
        this.$refs.form.validate(async (isSuccess) => {
          if (!isSuccess) return;
          this.isExamining = true;
          this.handleReject();
        });
      } else {
        this.isExamining = true;
        this.handlePass();
      }
    },
    async handlePass() {
      const { isError } = await passApply({ apply_id: this.id, review_remark: this.post.review_remark });
      this.isExamining = false;

      if (!isError) RouteConfig.voucherAddApplyList.push({ query: { apply_states: applyStates.review.value } });
    },
    async handleReject() {
      const { isError } = await update(
        this.id,
        { review_remark: this.post.review_remark, update_time: this.post.update_time },
      );
      this.isExamining = false;

      if (!isError) RouteConfig.voucherAddApplyList.push({ query: { apply_states: applyStates.review.value } });
    },
    async handleRevoke() {
      if (this.isRevoking) return;

      this.isRevoking = true;
      const { isError } = await destroy(this.id, { update_time: this.post.update_time });
      this.isRevoking = false;
      if (isError) return;

      RouteConfig.voucherAddApplyList.push({ query: { apply_states: applyStates.revoked.value } });
    },
  },
};
</script>
